import {Injectable, Injector} from '@angular/core';
import {CoreActionCreatorService} from "./core-action-creator.service";
import {Observable} from "rxjs/Observable";
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from "@angular/common/http";
import {of} from 'rxjs/observable/of';
import {catchError, filter, finalize, flatMap, map, take} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {get, isEmpty} from 'lodash';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/take';
import {SessionRefreshService} from "@ispm/core/services/session-refresh.service";
import {IEService} from "@ispm/shared/services/IE.service";
import {AuthenticationService} from "@ispm/user/services/authentication.service";
import {Router} from "@angular/router";
import {CryptoService} from "@ispm/migration/services/crypto.service";

export type ExtendedHttpResponse<T> = HttpResponse<T> & { json: () => T };

@Injectable()
export class HttpInterceptorService {
  public static timeZoneOffsetInMinutes = ((new Date()).getTimezoneOffset()).toString();
  public static readonly SHOW_MESSAGE_ERROR_CODES: number[] = [502, 504];
  public static readonly DEFAULT_ORGANIZATION_LANGUAGE: string = 'en-US';
  public static readonly VALUESET: string = 'valueset';
  public static readonly VALUESET_FLATTEN_AND_FILTER: string = 'valuesetflattenandfilter';
  public static readonly LOGIN_ROUTE: string = '/auth/login';

  private static encodableParams: string[] = ['name', 'identifier'];

  protected options: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    observe: 'response';
    params?: HttpParams | {
      [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  } = {
    observe: 'response'
  };

  get organizationLanguage() {
    return this.store$.select('users').
    take(1).
    catch((err) => {
      throw new Error();
    }).
    pipe(
      filter(users => !!users)
    ).toPromise();
  }
  get currentApp() {
    return this.store$.select('navigation').
    take(1).
    catch((err) => {
      throw new Error();
    }).
    pipe(
        filter(data => !!data),
        map(navigation => navigation.currentApp)
    ).toPromise();
  }
  private _organizationLanguage: string;

  constructor(
    protected _http: HttpClient,
    protected injector: Injector,
    protected coreAC: CoreActionCreatorService,
    protected store$: Store<any>,
    protected sessionRefreshService: SessionRefreshService,
    protected ieService: IEService,
    protected cryptoService: CryptoService,
    private router: Router) {
  }
  private get authService(): AuthenticationService {
    return this.injector.get(AuthenticationService);
  }

  public static getParameterByName(name) {
    let match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
    return match && decodeURIComponent(decodeURIComponent(match[1].replace(/\+/g, ' ')));
  };

  getOrganization(currentUser, useOriginalOrg: boolean = false){
    if(!currentUser){
      return null;
    }
    return useOriginalOrg ? currentUser.originalOrganization : currentUser.organization;
  }

  protected async buildHeaders(additionalHeaders:{[prop:string]:string} = {}, url: string, params: any, useOriginalOrg?: boolean): Promise<HttpHeaders> {
    let headers = new HttpHeaders();

    let currentUser = (await this.authService).user,
      external_id = currentUser ? currentUser.hsdpId : null,
      organization = this.getOrganization(currentUser, useOriginalOrg);

    let xsrf,selectedFacilities = null;

    if (currentUser && currentUser.xsrf) {
      xsrf = currentUser.xsrf;
    }
    if (currentUser && currentUser.selectedFacilities) {
      selectedFacilities = currentUser.selectedFacilities;
    }

    if (HttpInterceptorService.getParameterByName("u-code")) {
      xsrf = HttpInterceptorService.getParameterByName("u-code");
    }

    if (HttpInterceptorService.getParameterByName("orgId")) {
      organization = HttpInterceptorService.getParameterByName("orgId");
    }

    if (HttpInterceptorService.getParameterByName("u-external-id")) {
      external_id = HttpInterceptorService.getParameterByName("u-external-id");
    }

    if (HttpInterceptorService.getParameterByName("orgId") && HttpInterceptorService.getParameterByName("aux_token")) {
      headers = headers.append('aux_token',HttpInterceptorService.getParameterByName("aux_token"));
    }
    /**
     *  Setting the organization language from store. If not available assigning the default value as en-US
     */
    await this.organizationLanguage
      .then( (users:any) => {
        if (users.organizationConfiguration && users.organizationConfiguration.language && users.organizationConfiguration.language.code)
          this._organizationLanguage =  users.organizationConfiguration.language.code;
        else
          this._organizationLanguage = HttpInterceptorService.DEFAULT_ORGANIZATION_LANGUAGE;
      }).catch(
        () =>  this._organizationLanguage = HttpInterceptorService.DEFAULT_ORGANIZATION_LANGUAGE
      );


    const isCache = url.indexOf('cacheId') !== -1 || (params && params.cacheId !== undefined);
    if(!isCache) {
      headers = headers.append('Cache-Control', 'no-cache');
      if(this.ieService.isIE()){
        headers = headers.append('Pragma', 'no-cache');
        headers = headers.append('Expires', 'Thu, 01 Jan 1970 00:00:00 GMT');
      }
    }
    headers = headers.append('timezone_offset', HttpInterceptorService.timeZoneOffsetInMinutes);
    headers = headers.append('Accept-Language', this._organizationLanguage);

    if (organization){
      headers = headers.append('organization', organization);
      if(!(isEmpty(selectedFacilities)) && !(isEmpty(currentUser))) {
        headers = headers.append('selectedFacilities',this.getSelectedFacilities(selectedFacilities,currentUser));
      }
    }

    if (xsrf) {
      headers = headers.append('xsrf', xsrf);
    }

    if (external_id) {
      headers = headers.append('external_id', external_id);
    }

    if (currentUser && currentUser.permissions && !headers.has('aux_token')) {
      const hash = this.cryptoService.hashSha256(JSON.stringify(currentUser.permissions.sort()) + '_' + (currentUser.roleId || ''));
      headers = headers.append('aux_token', hash);
    }

    for(const header in additionalHeaders){
      headers = headers.append(header,additionalHeaders[header]);
    }

    return headers;
  }

  public buildUrl(url: string, params: any = {}, addLocaleId: boolean = false): Observable<string> {
    let firstIteration = true, symbol = '?';

    for (let paramKey in params) {
      const paramValue = this.encodeParam(paramKey, params[paramKey]);
      url += `${symbol}${paramKey}=${paramValue}`;
      if (firstIteration) {
        symbol = '&';
        firstIteration = false;
      }
    }
    return addLocaleId ? this.addLocaleIdParamsToURL(url) : of(url);
  }



  public normalizeBody(body:{[prop:string]:string} = {} ): Observable<any>{
    return of(body);
  }

  /**
   *
   * @param url
   * @param params
   * @param toggleLoader
   * @param headers
   * @param options
   * @param showErrorMessage
   * @param addOrganizationLocaleId
   * @param useOriginalOrgId - in case of org override (admin) keep use the original org ID and not the overrided one
   */
  public get<T = any>(url: string, params: any = { }, toggleLoader: boolean = false, headers: { [ prop: string ]: string } = { },
                      options = { }, showErrorMessage: boolean = true, addOrganizationLocaleId: boolean = false,
                      useOriginalOrgId: boolean = false): Observable<ExtendedHttpResponse<T>>
  {
    return this.buildUrl(url, params, addOrganizationLocaleId).pipe(
      flatMap(async ( _url: string ) => {
        this.setLoader(toggleLoader, true);
        this.sessionRefreshService.apiCalled(url);

        options = { ...options, ...this.options };
        options['headers'] =  await this.buildHeaders(headers, url, params, useOriginalOrgId);
        return _url;
      }),
      flatMap((_url) => {
        return this._http.get<any>(_url, options);
      }),
      map(response => {
        if (response.status === 205) {
          this.reportError(response.status, 'GET', url, params, headers);
          this.displayReloadModal();
          throw new Error('Stale content detected');
        }
        return response;
      }),
      map(response => {
        return this.addJsonFunction<T>(response);
      }),
      catchError((err) => {
      console.error('HTTP interceptor Service : get() : url : ',url,' : Error', err.status,'-', err.message);
      return this.responseErrorHandler(err, showErrorMessage)}),
      finalize(() => {
        this.setLoader(toggleLoader, false);
      })
    )
  }
  public post<T = any>(url, body, toggleLoader: boolean = false, headers: { [ prop: string ]: string } = { },
                       params: { [ prop: string ]: string } = { },
                       options = { },
                       showErrorMessage: boolean = false,
                       useOriginalOrg?:boolean): Observable<ExtendedHttpResponse<T>>
  {
    this.setLoader(toggleLoader, true);
    this.sessionRefreshService.apiCalled(url);
    return this.buildUrl(url,params).pipe(
      map(_url=>url=_url),
      flatMap(async ( _url: string ) => {
        options = { ...options, ...this.options };
        options['headers'] =  await this.buildHeaders(headers, url, params, useOriginalOrg);
        return _url;
      }),
      flatMap(()=>this.normalizeBody(body)),
      flatMap(body=>this._http.post<T>(url, body, {...this.options, ...options})),
      map(response => {
        if (response.status === 205) {
          this.reportError(response.status, 'POST', url, params, headers, body);
          this.displayReloadModal();
          throw new Error('Stale content detected');
        }
        return response;
      }),
      map(response => {
        return this.addJsonFunction<T>(response);
      }),
      catchError((err) => {
        console.error('HTTP interceptor Service : post(): url : ',url,' : Error', err.status,'-', err.message,'-');
        return this.responseErrorHandler(err, showErrorMessage,url)}),
      finalize(() => {
        this.setLoader(toggleLoader, false);
      })
    );
  }

  public put<T = any>(url, body, toggleLoader: boolean = false,
                      headers: { [ prop: string ]: string } = { }, params: { [ prop: string ]: string } = { },
                      options = { },
                      showErrorMessage: boolean = false): Observable<ExtendedHttpResponse<T>>
  {
    this.setLoader(toggleLoader, true);
    this.sessionRefreshService.apiCalled(url);

    return this.buildUrl(url,params).pipe(
      map(_url=>url=_url),
      flatMap(async ( _url: string ) => {
        options = { ...options, ...this.options };
        options['headers'] =  await this.buildHeaders(headers, url, params);

        return _url;
      }),
      flatMap(()=>this.normalizeBody(body)),
      flatMap(body=>this._http.put<T>(url, body, {...this.options, ...options})),
      map(response => {
        if (response.status === 205) {
          this.reportError(response.status, 'PUT', url, params, headers, body);
          this.displayReloadModal();
          throw new Error('Stale content detected');
        }
        return response;
      }),
      map(response => {
        return this.addJsonFunction<T>(response);
      }),
      catchError((err) => {
        console.error('HTTP interceptor Service : put() : url : ',url,' :  Error', err.status,'-', err.message);
        return this.responseErrorHandler(err, showErrorMessage)}),
      finalize(() => {
        this.setLoader(toggleLoader, false);
      })
    );
  }

  public delete(url: string, params: any = { }, toggleLoader: boolean = false,
                headers: { [ prop: string ]: string } = { },
                options = { },
                showErrorMessage: boolean = false): any
  {
    this.setLoader(toggleLoader, true);
    this.sessionRefreshService.apiCalled(url);

    return this.buildUrl(url, params).pipe(
      flatMap(async ( _url: string ) => {
        options = { ...options, ...this.options };
        options['headers'] =  await this.buildHeaders(headers, url, params);
        return _url;
      }),
      flatMap((_url)=>this._http.delete(_url, {...this.options, ...options})),
      map(response => {
        if (response.status === 205) {
          this.reportError(response.status, 'DELETE', url, params, headers);
          this.displayReloadModal();
          throw new Error('Stale content detected');
        }
        return response;
      }),
      map(response => {
        this.addJsonFunction(response);
        return response;
      }),
      catchError((err) => {
        console.error('HTTP interceptor Service : delete() : url : ',url,' : Error', err.status,'-', err.message);
        return this.responseErrorHandler(err, showErrorMessage)}),
      finalize(() => {
        this.setLoader(toggleLoader, false);
      })
    )
  }

  public getFile<T = any>(url: string, params: any = { }, toggleLoader: boolean = false,
                          headers: { [ prop: string ]: string } = { },
                          options = { },
                          showErrorMessage: boolean = true): Observable<ExtendedHttpResponse<T>>
  {
    this.setLoader(toggleLoader, true);
    return this.buildUrl(url, params).pipe(
      flatMap(async ( _url: string ) => {
        options = { ...options, ...this.options };
        options['headers'] =  await this.buildHeaders(headers, _url, params);
        return _url;
      }),
      flatMap((_url)=>this._http.get<T>(_url, {...this.options, ...options})),
      map(response => {
        if (response.status === 205) {
          this.reportError(response.status, 'getFile', url, params, headers);
          this.displayReloadModal();
          throw new Error('Stale content detected');
        }
        return response;
      }),
      map(response => {
        return this.addJsonFunction<T>(response);
      }),
      catchError((err) => {
        console.error('HTTP interceptor Service : getFile() : url : ',url,' : Error', err.status,'-', err.message);
        return this.responseErrorHandler(err, showErrorMessage)}),
      finalize(() => {
        this.setLoader(toggleLoader, false);
      })
    )
  }

  private _loaderCounter = 0;
  protected setLoader(toggleLoader, status) {
    if (toggleLoader) {
      this._loaderCounter += status ? 1 : -1;
      this.coreAC.setLoader(this._loaderCounter > 0);
    }
  }

  protected  responseErrorHandler(err, showErrorMessage: boolean, url?: string) {
    if(showErrorMessage && HttpInterceptorService.SHOW_MESSAGE_ERROR_CODES.includes(err.status)){

      this.coreAC.setErrorModal(true,'Partial information got loaded', 'Only part of the data was loaded, click on the reload button to reload the page', 'Reload');
    }
    switch (err.status) {

      case 401:
        console.info(new Date().toUTCString() + ": Unauthorized: " + err.message);
        if (url && url === HttpInterceptorService.LOGIN_ROUTE) {
          this.authService.logout({refresh: false, callFromResponseHandler: true});
        } else {
          this.authService.logout({refresh: true, callFromResponseHandler: true});
        }
        break;
      case 403:
        this.coreAC.setErrorModal(true,'Access denied', 'You do not have permissions to perform this action. Returning to home page.', 'OK',( )=> {
        //   const _routeHandler:  = this.routerManagerService;
        //   _routeHandler.gotohome();
          this.router.navigateByUrl('login');
        } );
        break;
    }
    return Observable.throw(err);
  }

  protected addJsonFunction<T>(response): ExtendedHttpResponse<T> {
    response.json = function () {
      return this.body;
    };
    return response;
  }

  protected displayReloadModal(): void {
    this.coreAC.setLoader(false);
    this.coreAC.setErrorModal(true, 'Stale content detected', 'Stale content was detected. Please reload the page');
  }

  private reportError(status: number, operation: string, url: string, params: any, headers: any, body?: string) {
    console.error(`Received response ${status} for request: ${operation} ${url}\n`
      + this.getErrorLine("Params", params)
      + this.getErrorLine("Headers", headers)
      + this.getErrorLine("Body", body));
  }

  private getErrorLine(msg: string, object: any) {
    return object ? (msg + ": " + JSON.stringify(object) + "\n") : "";
  }

  /**
   *  @param url
   *
   *  Checking whether url has name query parameter or not. If Query parameter is not available changing the valueset name to Query parameter name itself
   */
  protected addLocaleIdParamsToURL(url:string): Observable<string> {
    const localeParams = this._organizationLanguage === HttpInterceptorService.DEFAULT_ORGANIZATION_LANGUAGE ?  HttpInterceptorService.DEFAULT_ORGANIZATION_LANGUAGE : `${this._organizationLanguage},${HttpInterceptorService.DEFAULT_ORGANIZATION_LANGUAGE}`
    if ((url.toLowerCase().indexOf (HttpInterceptorService.VALUESET) || url.toLowerCase().indexOf(HttpInterceptorService.VALUESET_FLATTEN_AND_FILTER)) !== -1) {
      url = url.includes('?') ?  `${url}&locale=${localeParams}&_sort:desc=locale` : `${url}?locale=${localeParams}&_sort:desc=locale`
    }
    return of(url);
  }

  private getSelectedFacilities(selectedFacility: Array<any>, currentUser): string {
    let selectedFacilityString = "";
    const facility = get(selectedFacility, '0.id');
    if (facility === "all") {
      let currentAssociatedActiveFacilities = get(currentUser, 'currentAssociatedActiveFacilities');
      selectedFacilityString = Array.prototype.map.call(currentAssociatedActiveFacilities, s => s.id).toString();
    } else {
      selectedFacilityString = facility;
    }
    return selectedFacilityString;
  }

  // The unique difference is that encodeURI() function encodes special characters, except: , / ? : @ & = + $ #
  // whereas encodeURIComponent() function encodes special characters and in additional the characters which encodeURI doesn't encode!
  private encodeParam(paramName: string, paramValue: string): string {
    return HttpInterceptorService.encodableParams.some(item => paramName.toLowerCase().includes(item.toLowerCase())) ? encodeURIComponent(paramValue) : paramValue;
  }

}
